/**
 * Styremøter — board meetings.  MKK adoption, module 18 (C2-board-meetings; docs/platform-implementation/BOARD_MEETINGS.md).
 *
 * The call, the agenda, who came, what was decided, and the minutes. A meeting goes draft → called → held →
 * minutes_sent, and may be cancelled from draft or called. Its agenda items are numbered «n/yy» within the year the
 * meeting is held in, and stay editable until the minutes are sent. Every item that ends «decided» is a line in the
 * decision register — the one list the board keeps coming back to. Behind the `board_meetings` capability. Two
 * permissions (access.js):
 *   board.read      see the meetings, the agenda and the decisions; set one's OWN attendance
 *   board.manage    everything else: the meeting, its items, everybody's attendance, the call, the minutes
 *
 * The minutes are rendered by apps/api/src/receipt-pdf.js, which writes Helvetica in WinAnsi: Latin letters only, so a
 * title or a decision written in Arabic or Urdu comes out as «?» — minutesLines() says so on the page.
 *
 * Pure: no I/O, no clock — «now» and the mosque's zone are passed in.
 */
const { isId, cap, oneLine, text } = require('./fields');
const Day = require('./day');
const tz = require('./tz');
const Planner = require('./planner');

const STATUSES = ['draft', 'called', 'held', 'minutes_sent', 'cancelled'];
/** From each status, where a meeting may go next. */
const NEXT = { draft: ['called', 'cancelled'], called: ['held', 'cancelled'], held: ['minutes_sent'], minutes_sent: [], cancelled: [] };
const DECISION_KINDS = ['decided', 'noted', 'deferred', 'rejected'];
const ATTENDANCE = ['present', 'absent', 'excused'];
const MAX_TITLE = 120, MAX_PLACE = 120, MAX_MINUTES = 50000;
const MAX_ITEM_TITLE = 160, MAX_BACKGROUND = 5000, MAX_PROPOSAL = 2000, MAX_DECISION = 2000, MAX_NOTE = 200;
const MAX_ITEMS = 100, MAX_MEETINGS = 2000, MAX_QUERY = 120;
const MEETING_FIELDS = ['title', 'startsAt', 'endsAt', 'place', 'plannerItemId'];
const ITEM_FIELDS = ['title', 'background', 'proposal', 'decision', 'decisionKind', 'responsibleStaffId', 'dueOn'];
const NUMBER_RE = /^(\d{1,4})\/(\d{2})$/;

const STATUS_NO = { draft: 'Utkast', called: 'Innkalt', held: 'Avholdt', minutes_sent: 'Referat sendt', cancelled: 'Avlyst' };
const KIND_NO = { decided: 'Vedtatt', noted: 'Tatt til orientering', deferred: 'Utsatt', rejected: 'Avvist' };
const ATTENDANCE_NO = { present: 'Til stede', absent: 'Fraværende', excused: 'Meldt forfall' };

/**
 * An instant: a date, a clock time and a zone designator (Z or ±hh:mm), stored as UTC ISO. A wall time without a zone
 * is refused: the server's own zone must never decide when a meeting starts (the panel sends toISOString()).
 */
const INSTANT_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,3})?)?(Z|[+-]\d{2}:\d{2})$/;
function instant(v, label, required) {
  if (v === undefined || v === null || v === '') return required ? { ok: false, message: `Velg ${label}.` } : { ok: true, value: null };
  if (typeof v !== 'string' || !INSTANT_RE.test(v) || Number.isNaN(Date.parse(v))) {
    return { ok: false, message: `${cap(label)} må være en gyldig dato og et klokkeslett.` };
  }
  return { ok: true, value: new Date(v).toISOString() };
}
const optionalDay = (v, label) => (v === undefined || v === null || v === '' ? { ok: true, value: null }
  : Day.valid(v) ? { ok: true, value: v } : { ok: false, message: `${cap(label)} må være en gyldig dato (ÅÅÅÅ-MM-DD).` });
const optionalId = (v, label) => (v === undefined || v === null || v === '' ? { ok: true, value: null }
  : isId(v) ? { ok: true, value: v } : { ok: false, message: `Ukjent ${label}.` });
const object = (input) => (input && typeof input === 'object' && !Array.isArray(input) ? input : null);
const unknownOf = (src, fields) => Object.keys(src).filter((k) => !fields.includes(k) && k !== 'version');
const changedOf = (base, out, fields) => (base ? fields.filter((k) => (base[k] ?? null) !== (out[k] ?? null)) : fields.filter((k) => out[k] != null));

/**
 * A meeting as the panel sends it: title, when it starts (an instant), optionally when it ends and where. `base` is
 * the meeting as it is now (a change), or nothing (a new one). → { ok, value, changed } | { ok: false, errors }
 */
function meetingInput(input, base = null) {
  const src = object(input);
  if (!src) return { ok: false, errors: { meeting: 'Ugyldig møte.' } };
  const unknown = unknownOf(src, MEETING_FIELDS);
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const cur = base || { title: undefined, startsAt: undefined, endsAt: null, place: null, plannerItemId: null };
  const pick = (k) => (Object.prototype.hasOwnProperty.call(src, k) ? src[k] : cur[k]);
  const errors = {}, out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  take('title', oneLine(pick('title'), 'en tittel', MAX_TITLE));
  take('startsAt', instant(pick('startsAt'), 'når møtet starter', true));
  take('endsAt', instant(pick('endsAt'), 'når møtet slutter', false));
  take('place', oneLine(pick('place'), 'stedet', MAX_PLACE, false));
  // The meeting series it belongs to (work hub, 2026-10-31): a planner item, or none.
  take('plannerItemId', optionalId(pick('plannerItemId'), 'møteserie'));
  if (!errors.startsAt && !errors.endsAt && out.endsAt && out.endsAt <= out.startsAt) errors.endsAt = 'Møtet må slutte etter at det starter.';
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, value: out, changed: changedOf(base, out, MEETING_FIELDS) };
}

/**
 * An agenda item: title, the background, the proposal, and — once the meeting is held — the decision, its kind, who
 * is responsible and by when. A decision kind without a decision text is fine («tatt til orientering»); a decision text
 * without a kind is not: the register needs to know what the words are.
 */
function itemInput(input, base = null) {
  const src = object(input);
  if (!src) return { ok: false, errors: { item: 'Ugyldig sak.' } };
  const unknown = unknownOf(src, ITEM_FIELDS);
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const cur = base || { title: undefined, background: null, proposal: null, decision: null, decisionKind: null, responsibleStaffId: null, dueOn: null };
  const pick = (k) => (Object.prototype.hasOwnProperty.call(src, k) ? src[k] : cur[k]);
  const errors = {}, out = {};
  const take = (k, r) => { if (r.ok) out[k] = r.value; else errors[k] = r.message; };
  take('title', oneLine(pick('title'), 'en tittel på saken', MAX_ITEM_TITLE));
  take('background', text(pick('background'), 'bakgrunnen', MAX_BACKGROUND));
  take('proposal', text(pick('proposal'), 'forslaget', MAX_PROPOSAL));
  take('decision', text(pick('decision'), 'vedtaket', MAX_DECISION));
  const kind = pick('decisionKind');
  if (kind === undefined || kind === null || kind === '') out.decisionKind = null;
  else if (!DECISION_KINDS.includes(kind)) errors.decisionKind = 'Velg vedtatt, tatt til orientering, utsatt eller avvist.';
  else out.decisionKind = kind;
  if (!errors.decision && !errors.decisionKind && out.decision && !out.decisionKind) errors.decisionKind = 'Si hva vedtaket er: vedtatt, tatt til orientering, utsatt eller avvist.';
  take('responsibleStaffId', optionalId(pick('responsibleStaffId'), 'ansvarlig'));
  take('dueOn', optionalDay(pick('dueOn'), 'fristen'));
  if (Object.keys(errors).length) return { ok: false, errors };
  return { ok: true, value: out, changed: changedOf(base, out, ITEM_FIELDS) };
}

/** One person's attendance: a status and an optional note («kom 19:30»). */
function attendanceInput(input) {
  const src = object(input);
  if (!src) return { ok: false, errors: { attendance: 'Ugyldig oppmøte.' } };
  const unknown = Object.keys(src).filter((k) => !['status', 'note'].includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  if (!ATTENDANCE.includes(src.status)) return { ok: false, errors: { status: 'Velg til stede, fraværende eller meldt forfall.' } };
  const note = oneLine(src.note, 'merknaden', MAX_NOTE, false);
  if (!note.ok) return { ok: false, errors: { note: note.message } };
  return { ok: true, value: { status: src.status, note: note.value } };
}

/** Whether a meeting may go from `from` to `to`. The same status is not a transition. */
const transition = (from, to) => Boolean(NEXT[from]) && NEXT[from].includes(to);
/** The agenda, the attendance and the minutes may change until the minutes are sent; a cancelled meeting is closed too. */
const editable = (status) => ['draft', 'called', 'held'].includes(status);
/** A meeting may be deleted only as a draft (nobody was called); after that it is cancelled, and kept. */
const deletable = (status) => status === 'draft';
/** A decision may be written on an item once the meeting is held (not before: nothing was decided yet). */
const decidable = (status) => status === 'held';

/** The two-digit year a meeting is numbered in: the year it starts, in the mosque's zone. */
const yearOf = (startsAt, zone) => tz.dateIn(startsAt, zone).slice(2, 4);
/** The next item number «n/yy»: one more than the highest n already used in that year, from 1. */
function nextNumber(existing, yy) {
  let max = 0;
  for (const n of existing || []) {
    const m = NUMBER_RE.exec(String(n || ''));
    if (m && m[2] === yy) max = Math.max(max, Number(m[1]));
  }
  return `${max + 1}/${yy}`;
}

/** A new order for the items: every id once, nothing missing, nothing foreign. → { ok, value: [ids] } | { ok: false, message } */
function reorder(ids, current) {
  if (!Array.isArray(ids) || ids.some((x) => !isId(x))) return { ok: false, message: 'Ugyldig rekkefølge.' };
  const have = new Set(current);
  if (ids.length !== have.size || new Set(ids).size !== ids.length || ids.some((x) => !have.has(x))) return { ok: false, message: 'Rekkefølgen må nevne hver sak én gang.' };
  return { ok: true, value: ids };
}

/** Minutes text: several lines, at most MAX_MINUTES. */
function minutesInput(input) {
  const src = object(input);
  if (!src) return { ok: false, errors: { minutes: 'Ugyldig referat.' } };
  const unknown = Object.keys(src).filter((k) => k !== 'minutesText' && k !== 'version');
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const r = text(src.minutesText, 'referatet', MAX_MINUTES);
  if (!r.ok) return { ok: false, errors: { minutesText: r.message } };
  return { ok: true, value: { minutesText: r.value } };
}

const two = (n) => String(n).padStart(2, '0');
/** «24.10.2026 kl. 19:00» in the mosque's zone. */
function whenText(iso, zone) {
  if (!iso) return '';
  const p = tz.partsIn(iso, zone);
  return `${two(p.day)}.${two(p.month)}.${p.year} kl. ${two(p.hour)}:${two(p.minute)}`;
}
const dayText = (d) => { const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(d || '')); return m ? `${m[3]}.${m[2]}.${m[1]}` : ''; };
/** «  Label: first line», then the rest of the lines indented under it. */
function labelled(L, label, body) {
  body.split('\n').forEach((l, i) => L.push(i === 0 ? `  ${label}: ${l}` : `  ${' '.repeat(label.length + 2)}${l}`));
}

/**
 * The minutes as lines for receipt-pdf.render(): the title, when and where, who came, each item with its background,
 * proposal, decision, kind, responsible and due day, the minutes text, and two signature lines. `attendance` is
 * [{ name, status, note }] in the order to print; `items` in agenda order with `number`. Latin only — the renderer
 * turns anything else into «?», and the page says so.
 */
function minutesLines({ meeting, items, attendance, mosqueName, zone, confirmations = null }) {
  const L = [];
  L.push(`Referat fra styremøte — ${mosqueName}`);
  L.push(`${meeting.title}`);
  L.push(`Tid: ${whenText(meeting.startsAt, zone)}${meeting.endsAt ? ` – ${whenText(meeting.endsAt, zone)}` : ''}`);
  if (meeting.place) L.push(`Sted: ${meeting.place}`);
  L.push('');
  L.push('Til stede');
  const present = (attendance || []).filter((a) => a.status === 'present');
  const away = (attendance || []).filter((a) => a.status !== 'present');
  if (!present.length) L.push('  (ingen registrert)');
  for (const a of present) L.push(`  ${a.name}${a.note ? ` (${a.note})` : ''}`);
  if (away.length) {
    L.push('Forfall');
    for (const a of away) L.push(`  ${a.name} — ${ATTENDANCE_NO[a.status] || a.status}${a.note ? ` (${a.note})` : ''}`);
  }
  L.push('');
  L.push('Saker');
  if (!(items || []).length) L.push('  (ingen saker)');
  for (const it of items || []) {
    L.push('');
    L.push(`Sak ${it.number}  ${it.title}`);
    if (it.background) labelled(L, 'Bakgrunn', it.background);
    if (it.proposal) labelled(L, 'Forslag', it.proposal);
    if (it.decisionKind) L.push(`  Vedtak (${KIND_NO[it.decisionKind] || it.decisionKind})${it.decision ? ':' : ''}`);
    if (it.decision) for (const l of it.decision.split('\n')) L.push(`    ${l}`);
    if (it.responsibleName || it.dueOn) L.push(`  Ansvarlig: ${it.responsibleName || '—'}${it.dueOn ? `, frist ${dayText(it.dueOn)}` : ''}`);
  }
  if (meeting.minutesText) {
    L.push('');
    L.push('Referat');
    for (const l of meeting.minutesText.split('\n')) L.push(l);
  }
  // «Bekreftet med Vipps» (owner 2026-10-29): the chair's and a board member's confirmations of the sent minutes, each as
  // vipps-signature.stampLine() wrote it (`confirmations` = { chair, board_member } as views { line, valid, sealed }).
  if (confirmations && (confirmations.chair || confirmations.board_member)) {
    L.push('');
    L.push('Protokollen er bekreftet med Vipps (identifisert med Vipps Login og bundet til innholdet over):');
    for (const [role, label] of [['chair', 'Styreleder'], ['board_member', 'Styremedlem']]) {
      const c = confirmations[role];
      L.push(`  ${label}: ${c ? `${c.line}${c.valid ? '' : ' (gjelder ikke lenger)'}${c.sealed ? ' · forseglet' : ''}` : 'ikke bekreftet'}`);
    }
  }
  L.push('');
  L.push('');
  L.push('Møteleder: ________________________________________');
  L.push('');
  L.push('');
  L.push('Referent:  ________________________________________');
  L.push('');
  L.push('Dokumentet er skrevet med latinske bokstaver; tekst i andre alfabeter vises som «?».');
  return L;
}

/**
 * «Bekreftet med Vipps» on board minutes (owner 2026-10-29): who may confirm the minutes of a meeting, and when.
 *   chair          a staff member whose mosque role is the chair (board_chair);
 *   board_member   a staff member whose mosque role is on the board (board_member, secretary, treasurer, board_chair).
 * Only once «Send referat» froze the minutes (status minutes_sent); each role once (a valid confirmation in the role
 * stops a second); never the same person in both roles. `me` = { id, role }; `stamps` = { chair, board_member }: the
 * ACTIVE confirmation in each role ({ staffUserId, valid }) or null. → { ok } | { ok: false, code, message }.
 * Admins and super admins are not on the board by their role and do not confirm minutes (an owner question).
 */
const CHAIR_ROLES = ['board_chair'];
const BOARD_ROLES = ['board_member', 'secretary', 'treasurer', 'board_chair'];
const MINUTES_ROLES = { chair: CHAIR_ROLES, board_member: BOARD_ROLES };
const minutesRefuse = (code, message) => ({ ok: false, code, message });
function minutesConfirmAllowed(meeting, role, me, { stamps = {} } = {}) {
  if (!meeting || !me || !isId(me.id)) return minutesRefuse('not_found', 'Fant ikke møtet.');
  if (!Object.prototype.hasOwnProperty.call(MINUTES_ROLES, role)) return minutesRefuse('invalid', 'Ukjent rolle.');
  if (meeting.status !== 'minutes_sent') return minutesRefuse('minutes_not_sent', 'Protokollen bekreftes når referatet er sendt.');
  if (!MINUTES_ROLES[role].includes(me.role)) {
    return minutesRefuse('not_eligible', role === 'chair' ? 'Bare styrelederen kan bekrefte protokollen som styreleder.'
      : 'Bare et styremedlem (styremedlem, sekretær, kasserer eller styreleder) kan bekrefte protokollen.');
  }
  const other = stamps[role === 'chair' ? 'board_member' : 'chair'];
  if (other && other.staffUserId === me.id) return minutesRefuse('four_eyes', 'Du har allerede bekreftet protokollen. En annen må bekrefte den andre rollen.');
  const mine = stamps[role];
  if (mine && mine.valid) return minutesRefuse('already_confirmed', 'Protokollen er allerede bekreftet i denne rollen.');
  return { ok: true };
}

/* ---------------------------------------------------------------- the meeting series (work hub, 2026-10-31) ---------------------------------------------------------------- */

/**
 * «Fast møtedag»: the board's regular meeting day as a planner item — weekly, every other week or monthly on a weekday,
 * at a time, in a place. seriesInput(body, today) → the planner itemInput() body for it (a `meeting` item whose series
 * starts on the next such weekday and runs SERIES_YEARS years), or { ok: false, errors }.
 *   freq      'weekly' | 'biweekly' | 'monthly'   (monthly: the same Nth weekday of the month)
 *   weekday   0–6 (0 = Sunday, as day.js counts)
 *   time      'HH:MM'; endTime optional; place optional; title optional (default «Styremøte»)
 */
const SERIES_FREQS = ['weekly', 'biweekly', 'monthly'];
const SERIES_YEARS = 2;
const SERIES_FIELDS = ['freq', 'weekday', 'time', 'endTime', 'place', 'title'];
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
function seriesInput(input, today) {
  const src = object(input);
  if (!src) return { ok: false, errors: { series: 'Ugyldig møteserie.' } };
  const unknown = Object.keys(src).filter((k) => !SERIES_FIELDS.includes(k));
  if (unknown.length) return { ok: false, errors: Object.fromEntries(unknown.map((k) => [k, 'Ukjent felt.'])) };
  const errors = {};
  if (!SERIES_FREQS.includes(src.freq)) errors.freq = 'Velg ukentlig, annenhver uke eller månedlig.';
  if (!Number.isInteger(src.weekday) || src.weekday < 0 || src.weekday > 6) errors.weekday = 'Velg en ukedag.';
  if (!TIME_RE.test(String(src.time || ''))) errors.time = 'Skriv klokkeslettet som TT:MM.';
  const et = src.endTime === undefined || src.endTime === null || src.endTime === '' ? null : src.endTime;
  if (et !== null && (!TIME_RE.test(String(et)) || et <= src.time)) errors.endTime = 'Sluttid må være etter starttid (TT:MM).';
  const place = oneLine(src.place, 'stedet', MAX_PLACE, false);
  if (!place.ok) errors.place = place.message;
  const title = src.title === undefined || src.title === null || String(src.title).trim() === '' ? { ok: true, value: 'Styremøte' } : oneLine(src.title, 'en tittel', MAX_TITLE);
  if (!title.ok) errors.title = title.message;
  if (!Day.valid(today)) errors.today = 'Ugyldig dag.';
  if (Object.keys(errors).length) return { ok: false, errors };
  const next = Day.add(today, (src.weekday - Day.weekday(today) + 7) % 7);
  let startsOn = next, recurrence;
  if (src.freq === 'monthly') {
    // The next month's same-Nth weekday as `next` — a fifth one (which «samme ukedag» cannot carry) falls back to the fourth.
    const n = Math.min(4, Math.ceil(Day.parse(next).d / 7));
    const p = Day.parse(next);
    startsOn = Planner.weekdayInMonth(p.y, p.m, src.weekday, n);
    if (startsOn < today) { const m = Day.addMonths(p.y, p.m, 1); startsOn = Planner.weekdayInMonth(m.y, m.m, src.weekday, n); }
    recurrence = { freq: 'monthly', interval: 1, by: 'weekday' };
  } else recurrence = { freq: 'weekly', interval: src.freq === 'biweekly' ? 2 : 1 };
  const u = Day.parse(startsOn);
  recurrence.until = Day.fmt(u.y + SERIES_YEARS, u.m, Math.min(u.d, Day.daysInMonth(u.y + SERIES_YEARS, u.m)));
  return { ok: true, value: { title: title.value, category: 'meeting', startsOn, startTime: src.time, endTime: et, location: place.value, recurrence } };
}

/** The series' rule in words: «hver tirsdag kl. 18:00», «annenhver tirsdag …», «første tirsdag i måneden …». */
const WEEKDAY_NO = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];
const NTH_NO = ['første', 'andre', 'tredje', 'fjerde'];
function seriesText(item) {
  if (!item || !item.recurrence || !Day.valid(item.startsOn)) return null;
  const r = item.recurrence, wd = WEEKDAY_NO[Day.weekday(item.startsOn)];
  const when = r.freq === 'weekly' ? (r.interval === 2 ? `annenhver ${wd}` : `hver ${wd}`)
    : r.freq === 'monthly' && r.by === 'weekday' ? `${NTH_NO[Math.min(3, Math.ceil(Day.parse(item.startsOn).d / 7) - 1)]} ${wd} i måneden`
    : Planner.describe(r, item);
  return `${when}${item.startTime ? ` kl. ${item.startTime}${item.endTime ? `–${item.endTime}` : ''}` : ''}${item.location ? `, ${item.location}` : ''}`;
}

/**
 * «Opprett neste møte»: the next meeting of a series, as a draft the API inserts. `series` is the planner item; `previous`
 * the latest meeting of the series with its items ({ meeting, items }) or null; `today` the mosque's day; `zone` its zone;
 * `held` the start instants of the meetings that already exist in the series (so the next is one that has no meeting
 * yet). The standard agenda is carried over from the previous meeting: every item whose decision set a due day still in
 * the future, and every deferred one, comes back as «Oppfølging: …» with the decision as its background.
 * → { ok, value: { title, startsAt, endsAt, place, plannerItemId, items: [{ title, background }] } } | { ok: false, code, message }
 */
function nextMeetingFrom(series, previous, today, { zone = null, taken = [], offsetFor } = {}) {
  if (!series || !series.recurrence || !Day.valid(series.startsOn)) return { ok: false, code: 'no_series', message: 'Styret har ingen fast møtedag ennå.' };
  if (!Day.valid(today)) return { ok: false, code: 'invalid_day', message: 'Ugyldig dag.' };
  const takenDays = new Set((taken || []).map((iso) => tz.dateIn(iso, zone)));
  const dates = Planner.occurrences(series, today, Day.add(today, 400), [], { offsetFor }).filter((o) => !o.cancelled && !takenDays.has(o.date));
  if (!dates.length) return { ok: false, code: 'no_next', message: 'Serien har ingen flere møtedager. Forleng den i årshjulet.' };
  const date = dates[0].date;
  const time = series.startTime || '18:00';
  const startsAt = tz.wallToIso(date, time, zone);
  const endsAt = series.endTime ? tz.wallToIso(date, series.endTime, zone) : null;
  const items = [];
  for (const it of (previous && previous.items) || []) {
    const open = (it.dueOn && Day.valid(it.dueOn) && it.dueOn >= today) || it.decisionKind === 'deferred';
    if (!open) continue;
    const head = `Oppfølging: ${it.title}`.slice(0, MAX_ITEM_TITLE);
    const bg = [`Fra sak ${it.number}${previous.meeting && previous.meeting.startsAt ? ` (${whenText(previous.meeting.startsAt, zone)})` : ''}.`,
      it.decision ? `Vedtak: ${it.decision}` : null, it.responsibleName ? `Ansvarlig: ${it.responsibleName}${it.dueOn ? `, frist ${dayText(it.dueOn)}` : ''}` : null]
      .filter(Boolean).join('\n').slice(0, MAX_BACKGROUND);
    items.push({ title: head, background: bg });
  }
  const p = Day.parse(date);
  return { ok: true, value: { title: `${series.title} ${p.d}.${p.m}.${p.y}`.slice(0, MAX_TITLE), startsAt, endsAt, place: series.location || null,
    plannerItemId: series.id || null, items, date } };
}

/** The decision register's filter: a search (≤120 characters) and an optional year. → { ok, value } | { ok: false, message } */
function decisionFilter(q = {}) {
  const text_ = String(q.q || '').trim().toLocaleLowerCase('nb');
  if (text_.length > MAX_QUERY) return { ok: false, message: 'Søket er for langt.' };
  let year = null;
  if (q.year !== undefined && q.year !== null && q.year !== '') {
    if (!/^\d{4}$/.test(String(q.year))) return { ok: false, message: 'Ugyldig år.' };
    year = String(q.year);
  }
  return { ok: true, value: { q: text_, year } };
}
/** Whether a decided item is in the register's answer: its number, title or decision contains the search; its year matches. */
function decisionMatches(d, f) {
  if (d.decisionKind !== 'decided') return false;
  if (f.year && String(d.meetingStartsOn || '').slice(0, 4) !== f.year) return false;
  if (!f.q) return true;
  return [d.number, d.title, d.decision].some((s) => String(s || '').toLocaleLowerCase('nb').includes(f.q));
}
/** Newest meeting first, then agenda order. */
const sortDecisions = (rows) => rows.slice().sort((a, b) => (a.meetingStartsAt < b.meetingStartsAt ? 1 : a.meetingStartsAt > b.meetingStartsAt ? -1 : a.sort - b.sort));

/** The list's split: upcoming = not yet held (draft or called) and starting from today, or in the past but still open; held = held, minutes sent or cancelled. */
function listSplit(meetings, now) {
  const nowIso = now instanceof Date ? now.toISOString() : String(now);
  const upcoming = meetings.filter((m) => m.status === 'draft' || m.status === 'called').sort((a, b) => (a.startsAt < b.startsAt ? -1 : 1));
  const held = meetings.filter((m) => !(m.status === 'draft' || m.status === 'called')).sort((a, b) => (a.startsAt < b.startsAt ? 1 : -1));
  return { upcoming, held, overdue: upcoming.filter((m) => m.startsAt < nowIso).map((m) => m.id) };
}

/** The audit fields (board_ namespace, audit.js BOARD_FIELDS): status words, counts and days — never a title or a decision's text. */
const meetingAudit = (m) => ({ board_status: m.status, board_starts_at: m.startsAt, board_place_set: Boolean(m.place), board_series_linked: Boolean(m.plannerItemId) });
const itemAudit = (it) => ({ board_item_number: it.number, board_decision_kind: it.decisionKind, board_responsible_id: it.responsibleStaffId, board_due_on: it.dueOn });
const attendanceAudit = (a) => ({ board_staff_id: a.staffUserId, board_attendance: a.status });
/** Whether a change to an item touches its decision (then the audit entry is board_item.decide, not update). */
const touchesDecision = (changed) => (changed || []).some((k) => ['decision', 'decisionKind', 'responsibleStaffId', 'dueOn'].includes(k));
/** The minutes' file name: «referat-styremote-2026-10-24.pdf» (ASCII, the day in the mosque's zone). */
const minutesFilename = (meeting, zone) => `referat-styremote-${tz.dateIn(meeting.startsAt, zone)}.pdf`;

module.exports = {
  STATUSES, NEXT, DECISION_KINDS, ATTENDANCE, STATUS_NO, KIND_NO, ATTENDANCE_NO,
  MAX_TITLE, MAX_PLACE, MAX_MINUTES, MAX_ITEM_TITLE, MAX_BACKGROUND, MAX_PROPOSAL, MAX_DECISION, MAX_NOTE, MAX_ITEMS, MAX_MEETINGS,
  MEETING_FIELDS, ITEM_FIELDS, isId,
  meetingInput, itemInput, attendanceInput, minutesInput, reorder,
  transition, editable, deletable, decidable, yearOf, nextNumber, touchesDecision,
  minutesLines, minutesFilename, CHAIR_ROLES, BOARD_ROLES, minutesConfirmAllowed, whenText, decisionFilter, decisionMatches, sortDecisions, listSplit,
  meetingAudit, itemAudit, attendanceAudit,
  SERIES_FREQS, SERIES_YEARS, seriesInput, seriesText, nextMeetingFrom,
};
